import {
  AuditFilterOptions,
  AuditLogEntry,
  AuditSnapshot,
  CrisisActivationRequest,
  CrisisActivationResult,
  CrisisScenario,
  DecisionOverrideContext,
  DecisionOverrideRequest,
  DecisionOverrideResult,
  PolicyComplianceSnapshot,
  PolicyEnforcementAction,
  PolicyEnforcementResult,
} from '@/types/entities/community_subpanel-elect/subpanelElectAuthority';

// Placeholder dataset used while backend integrations are defined.
// Mock data for executive actions
// In production, these would come from API endpoints

const mockDecisionOverrides: DecisionOverrideContext[] = [
  {
    id: 'override-001',
    communityId: 'comm-glastonbury',
    communityName: 'Community Landowners Association',
    department: 'Roads',
    issueSummary:
      'Emergency Road Management SubPanel has requested override: Winter snowplow contractor dispute over payment terms and service scope. Roads Department SubPanel-Elect Sample SubPanel Lead deadlocked with Winter Operations Sub-Elect Sample Operations Lead on whether to terminate current contractor (Big Sky Snowplow Services) mid-season after 3 service failures during critical snowstorms. $85,000 contract at stake. 127 residents without reliable access.',
    severity: 'critical',
    submittedAt: '2025-10-22T08:30:00Z',
    submittedAtRelative: '3 days ago',
    currentStatus: 'awaiting_override',
    recommendedOutcome: 'approve',
    requiresFollowUp: true,
  },
  {
    id: 'override-002',
    communityId: 'comm-glastonbury',
    communityName: 'Community Landowners Association',
    department: 'Governing Documents',
    issueSummary:
      'Governing Documents SubPanel deadlock: Proposed amendment to parcel transfer rules requires SubPanel-Elect override. SubPanel-Elect Lysander Pembroke and Documentation Sub-Elect Arabella Whitmore in conflict over interpretation of 1990 founding CCRs regarding designated voter transfer during estate settlements. 8 pending estate transfers frozen, affecting 23 stakeholders. Legal Sub-Elect Reginald Ashford has escalated to platform level due to potential liability exposure ($2.3M in affected property values).',
    severity: 'high',
    submittedAt: '2025-10-20T11:45:00Z',
    submittedAtRelative: '5 days ago',
    currentStatus: 'pending_review',
    recommendedOutcome: 'amend',
    requiresFollowUp: true,
  },
];

const mockCrisisScenarios: CrisisScenario[] = [
  {
    id: 'crisis-001',
    name: 'Physical security breach',
    description: 'On-site intrusion detected; coordinate lockdown and notify safety teams.',
    severity: 'critical',
    affectedCommunities: 3,
    recommendedProtocols: ['Activate lockdown', 'Notify law enforcement', 'Trigger SMS alerts'],
    responsePlaybookId: 'playbook-security-01',
    lastTestedAt: '14 days ago',
  },
  {
    id: 'crisis-002',
    name: 'Severe weather escalation',
    description: 'Storm warnings issued; initiate shelter guidance and vendor readiness.',
    severity: 'high',
    affectedCommunities: 7,
    recommendedProtocols: [
      'Send push alerts',
      'Coordinate maintenance crews',
      'Confirm vendor readiness',
    ],
    responsePlaybookId: 'playbook-weather-02',
    lastTestedAt: '21 days ago',
  },
];

const mockPolicySnapshots: PolicyComplianceSnapshot[] = [
  {
    policyId: 'policy-privacy-001',
    policyName: 'Tenant Privacy Safeguards',
    category: 'privacy',
    complianceRate: 86.4,
    nonCompliantCommunities: 5,
    lastAuditAt: 'Yesterday',
    riskLevel: 'medium',
  },
  {
    policyId: 'policy-security-002',
    policyName: 'Incident Response Playbooks',
    category: 'security',
    complianceRate: 68.2,
    nonCompliantCommunities: 11,
    lastAuditAt: '3 days ago',
    riskLevel: 'high',
  },
];

const mockAuditEntries: AuditLogEntry[] = [
  {
    id: 'audit-001',
    timestamp: new Date().toISOString(),
    scope: 'governance',
    severity: 'warning',
    actor: 'Subpanel Elect',
    action: 'decision_override',
    entityId: 'override-001',
    entityType: 'override_request',
    summary: 'Override executed for capital reserve allocation deadlock.',
    relatedCommunities: ['Sunset Gardens'],
    attachments: [],
  },
  {
    id: 'audit-002',
    timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
    scope: 'security',
    severity: 'critical',
    actor: 'Subpanel Elect',
    action: 'crisis_activation',
    entityId: 'crisis-001',
    entityType: 'crisis_playbook',
    summary: 'Security breach playbook activated across 3 communities.',
    relatedCommunities: ['Harbor Ridge', 'Lakeside Estates'],
    attachments: [],
  },
];

const mockAuditSnapshot: AuditSnapshot = {
  totalEntries: mockAuditEntries.length,
  criticalFindings: mockAuditEntries.filter((entry) => entry.severity === 'critical').length,
  lastOverrideAt: mockAuditEntries.find((entry) => entry.action === 'decision_override')?.timestamp,
  lastAuditRunAt: new Date().toISOString(),
};

export const subpanelElectAuthorityService = {
  async fetchDecisionOverrideTargets(): Promise<DecisionOverrideContext[]> {
    return Promise.resolve(mockDecisionOverrides);
  },

  async submitDecisionOverride(payload: DecisionOverrideRequest): Promise<DecisionOverrideResult> {
    return Promise.resolve({
      id: payload.targetId,
      overrideId: `executed-${payload.targetId}`,
      executedAt: new Date().toISOString(),
      executedBy: 'Subpanel Elect',
      finalOutcome:
        payload.requestedOutcome === 'reject'
          ? 'rejected'
          : payload.requestedOutcome === 'amend'
            ? 'amended'
            : 'approved',
      auditLogId: 'audit-override-latest',
    });
  },

  async fetchCrisisScenarios(): Promise<CrisisScenario[]> {
    return Promise.resolve(mockCrisisScenarios);
  },

  async activateCrisisScenario(payload: CrisisActivationRequest): Promise<CrisisActivationResult> {
    return Promise.resolve({
      activationId: `activation-${payload.scenarioId}`,
      triggeredAt: new Date().toISOString(),
      acknowledgedBy: Math.floor(Math.random() * 25),
      status: 'initiated',
    });
  },

  async fetchPolicySnapshots(): Promise<PolicyComplianceSnapshot[]> {
    return Promise.resolve(mockPolicySnapshots);
  },

  async enforcePolicy(payload: PolicyEnforcementAction): Promise<PolicyEnforcementResult> {
    return Promise.resolve({
      enforcementId: `enforcement-${payload.policyId}`,
      executedAt: new Date().toISOString(),
      affectedCommunities: Math.floor(Math.random() * 10) + 1,
      nextReviewAt: payload.followUpAt,
    });
  },

  async fetchAuditLog(
    _filters?: AuditFilterOptions,
  ): Promise<{ snapshot: AuditSnapshot; entries: AuditLogEntry[] }> {
    return Promise.resolve({
      snapshot: mockAuditSnapshot,
      entries: mockAuditEntries,
    });
  },
};

export type SubpanelElectAuthorityService = typeof subpanelElectAuthorityService;

export default subpanelElectAuthorityService;
